import { Notification } from '@/types/notification';
import { NotificationWire } from '../schemas/notification';

/**
 * Mapping functions to convert wire format (backend) to domain format (app)
 */

/**
 * Convert wire notification format to domain notification format
 * Handles type conversions: 0/1 -> boolean, ISO strings -> relative time, metadata extraction
 */
export function mapNotificationWireToDomain(wire: NotificationWire): Notification {
  // Convert read status from 0/1 to boolean
  const read = wire.read === 1;

  // Convert ISO datetime string to relative time string
  // For now, we'll use a simple format. In production, you might want to use a library like date-fns
  const time = formatRelativeTime(wire.created_at);

  // Extract user info from metadata if available (actor info)
  // Accept both full URLs and R2 keys -- imageUrl() in the frontend handles both formats
  const actorImage = wire.metadata?.actor_image as string | undefined;
  const avatar = actorImage && typeof actorImage === 'string' ? actorImage : '';

  const user = wire.actor_id && wire.metadata?.actor_name
    ? {
        id: wire.actor_id,
        name: wire.metadata.actor_name as string,
        avatar,
        ...(wire.metadata?.actor_username && {
          username: wire.metadata.actor_username as string,
        }),
      }
    : undefined;

  // Extract restaurant, dish, image from metadata
  const restaurant = wire.metadata?.restaurant_name as string | undefined;
  const dish = wire.metadata?.dish_name as string | undefined;
  const image = wire.metadata?.image_url as string | undefined;
  const distance = wire.metadata?.distance as string | undefined;

  // Extract rich notification data (check-in likes with menu item/business info)
  const menuItemId = wire.metadata?.menu_item_id as string | undefined;
  const menuItemName = wire.metadata?.menu_item_name as string | undefined;
  const businessId = wire.metadata?.business_id as string | undefined;
  const businessName = wire.metadata?.business_name as string | undefined;
  const referenceType = wire.reference_type ?? undefined;
  const referenceId = wire.reference_id ?? undefined;

  // Extract check-in preview data
  const ratingKey = wire.metadata?.rating_key as string | undefined;
  const checkInUserName = wire.metadata?.check_in_user_name as string | undefined;
  const checkInUserAvatar = wire.metadata?.check_in_user_avatar as string | undefined;
  const businessCity = wire.metadata?.business_city as string | undefined;
  const checkInOccurredAt = wire.metadata?.occurred_at as string | undefined;

  // Extract menu item preview data
  const menuItemCoverPhoto = wire.metadata?.mi_cover_photo as string | undefined;
  const menuItemCheckInCount = wire.metadata?.mi_check_in_count as number | undefined;

  const miNotForMe = wire.metadata?.mi_rating_not_for_me as number | undefined;
  const miOkay = wire.metadata?.mi_rating_okay as number | undefined;
  const miTryIt = wire.metadata?.mi_rating_try_it as number | undefined;
  const miMustTry = wire.metadata?.mi_rating_must_try as number | undefined;
  const menuItemRatingDistribution = (miNotForMe != null || miOkay != null || miTryIt != null || miMustTry != null)
    ? { notForMe: miNotForMe ?? 0, okay: miOkay ?? 0, tryIt: miTryIt ?? 0, mustTry: miMustTry ?? 0 }
    : undefined;

  // Extract list notification data
  const listId = wire.metadata?.list_id as string | undefined;
  const listName = wire.metadata?.list_name as string | undefined;
  const listCoverImage = wire.metadata?.list_cover_image as string | undefined;
  const listDescription = wire.metadata?.list_description as string | undefined;
  const listOwnerName = wire.metadata?.list_owner_name as string | undefined;
  const listItemCount = wire.metadata?.list_item_count as number | undefined;
  const listMemberCount = wire.metadata?.list_member_count as number | undefined;

  // Strip duplicated actor name from content if present
  // If user is present and content starts with the actor's name, remove that prefix
  let content = wire.content;
  if (user && content.startsWith(user.name)) {
    // Remove the actor name prefix and any following whitespace/punctuation
    content = content.substring(user.name.length).trim();
    // Remove leading colon, dash, or space if present
    content = content.replace(/^[:-\s]+/, '').trim();
  }

  // Extract friend request action from server-persisted metadata
  const friendRequestAction = wire.metadata?.friend_request_action as 'accepted' | 'rejected' | undefined;

  return {
    id: wire.id,
    type: wire.type,
    user,
    content,
    restaurant,
    dish,
    image,
    distance,
    time,
    createdAt: wire.created_at,
    read,
    friendRequestAction,
    referenceType,
    referenceId,
    menuItemId,
    menuItemName,
    businessId,
    businessName,
    ratingKey,
    checkInUserName,
    checkInUserAvatar,
    businessCity,
    checkInOccurredAt,
    menuItemCoverPhoto,
    menuItemCheckInCount,
    menuItemRatingDistribution,
    listId,
    listName,
    listCoverImage,
    listDescription,
    listOwnerName,
    listItemCount,
    listMemberCount,
  };
}

/**
 * Format ISO datetime string to relative time string
 * Examples: "10 minutes ago", "2 hours ago", "1 day ago"
 */
function formatRelativeTime(isoString: string): string {
  const now = new Date();
  const date = new Date(isoString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return 'just now';
  } else if (diffMins < 60) {
    return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  } else {
    // For older notifications, show the date
    return date.toLocaleDateString();
  }
}
